import React, { useEffect, useState } from 'react';
import { Container, FloatingLabel, Form, Row } from 'react-bootstrap';
import { Button, Checkbox, Column, Select } from '../../../components';
import { SelectOption } from '../../../shared/components/input';
import { MortgageLabels } from '../../../shared/util/label';
import * as constant from '../../../shared/util/constant';
import { MortageDetailsModel } from '../../model/MortageDetailsModel';
import LookupService from '../../../Services/LookupService';
import * as tableConstant from '../../../shared/util/tableConstant';

export type MortageDetailsFormModel = MortageDetailsModel;

interface MortageDetailsProps {
	onSave?: (data: MortageDetailsFormModel) => void;
	onCancel?: () => void;
	initialData?: MortageDetailsFormModel | null;
}

const createDefaultFormData = (): MortageDetailsFormModel => ({
	companyName: '',
	type: '',
	phone: '',
	city: '',
	state: '',
	isActive: true,
});

const MortageDetails: React.FC<MortageDetailsProps> = ({ onSave, onCancel, initialData }) => {
	const [formData, setFormData] = useState<MortageDetailsFormModel>(createDefaultFormData());
	const [validated, setValidated] = useState(false);
	const [stateOptions, setStateOptions] = useState<SelectOption[]>([]);
	const [interestTypeOptions, setInterestTypeOptions] = useState<SelectOption[]>([]);

	useEffect(() => {
		setFormData(initialData ?? createDefaultFormData());
		setValidated(false);
	}, [initialData]);

	useEffect(() => {
		const loadDropdownOptions = async () => {
			const [usStateLookup, interestTypeLookup] = await Promise.all([
				LookupService.getLookupData(tableConstant.US_STATE_TABLE_ID),
				LookupService.getLookupData(tableConstant.INTEREST_TYPE_TABLE_ID),
			]);

			setStateOptions(
				(usStateLookup ?? []).map((item) => ({
					label: item.lookupCode,
					value: item.lookupCode,
				}))
			);

			setInterestTypeOptions(
				(interestTypeLookup ?? []).map((item) => ({
					label: item.lookupValue,
					value: item.id,
				}))
			);
		};

		loadDropdownOptions();
	}, []);

	const setField = (field: keyof MortageDetailsFormModel, value: string | boolean) => {
		setFormData((prev) => ({
			...prev,
			[field]: value,
		}));
	};

	const handleSave = () => {
		setValidated(true);
		if (!formData.companyName || !formData.type || !formData.phone || formData.phone.length !== 10) {
			return;
		}

		const selectedType = interestTypeOptions.find(({ value, label }) => value === formData.type || label === formData.type);
		onSave?.({
			...formData,
			type: selectedType?.value ?? formData.type,
		});
	};

	const handleCancel = () => {
		setFormData(createDefaultFormData());
		setValidated(false);
		onCancel?.();
	};

	return (
		<Container fluid className='p-3'>
			<Row>
				<Column lg={6} md={6} className='mb-3'>
					<FloatingLabel controlId='mortgageCompanyName' label={<>{MortgageLabels.COMPANY_NAME}<span className='text-danger ms-1'>*</span></>}>
						<Form.Control
							type='text'
							placeholder={MortgageLabels.COMPANY_NAME}
							className='texbox-input'
							value={formData.companyName}
							onChange={(event) => setField('companyName', event.currentTarget.value)}
							maxLength={51}
							isInvalid={validated && !formData.companyName}
						/>
						{validated && !formData.companyName && (
							<Form.Control.Feedback type='invalid'>
								{constant.FIELD_CANNOT_BE_BLANK}
							</Form.Control.Feedback>
						)}
					</FloatingLabel>
				</Column>
				<Column lg={6} md={6} className='mb-3'>
					<Select
						id='mortgageType'
						controlId='mortgageType'
						label={<>{MortgageLabels.TYPE}<span className='text-danger ms-1'>*</span></>}
						options={interestTypeOptions}
						defaultSelected={interestTypeOptions.filter(({ value, label }) => value === formData.type || label === formData.type)}
						onChange={(values) => setField('type', (values?.[0] as string) ?? '')}
						isInvalid={validated && !formData.type}
						invalidWarning={validated && !formData.type ? constant.FIELD_CANNOT_BE_BLANK : ''}
						placeholder={MortgageLabels.PLEASE_SELECT}
						multiple={false}
						dropup={false}
						showAsFloatingLabel={true}
						className='mb-3'
						sortOptions={false}
					/>
				</Column>
			</Row>

			<Row>
				<Column lg={6} md={6} className='mb-3'>
					<FloatingLabel controlId='mortgagePhone' label={<>{MortgageLabels.PHONE}<span className='text-danger ms-1'>*</span></>}>
						<Form.Control
							type='text'
							placeholder={MortgageLabels.PHONE}
							className='texbox-input'
							value={formData.phone}
							onChange={(event) => {
								const value = event.currentTarget.value.replace(/\D/g, '').slice(0, 10);
								setField('phone', value);
							}}
							minLength={10}
							maxLength={10}
							isInvalid={validated && (!formData.phone || formData.phone.length !== 10)}
						/>
						{validated && (!formData.phone || formData.phone.length !== 10) && (
							<Form.Control.Feedback type='invalid'>
								{formData.phone ? constant.CONTACT_NO_RANGE : constant.FIELD_CANNOT_BE_BLANK}
							</Form.Control.Feedback>
						)}
					</FloatingLabel>
				</Column>
				<Column lg={6} md={6} className='mb-3'>
					<FloatingLabel controlId='mortgageCity' label={MortgageLabels.CITY}>
						<Form.Control
							type='text'
							placeholder={MortgageLabels.CITY}
							className='texbox-input'
							value={formData.city}
							onChange={(event) => setField('city', event.currentTarget.value)}
							maxLength={19}
						/>
					</FloatingLabel>
				</Column>
			</Row>

			<Row>
				<Column lg={6} md={6} className='mb-3'>
					{stateOptions.length > 0 ? (
						<Select
							id='mortgageState'
							controlId='mortgageState'
							label={MortgageLabels.STATE}
							options={stateOptions}
							defaultSelected={stateOptions.filter(({ value }) => value === formData.state)}
							onChange={(values) => setField('state', (values?.[0] as string) ?? '')}
							placeholder={MortgageLabels.PLEASE_SELECT}
							multiple={false}
							dropup={false}
							showAsFloatingLabel={true}
							className='mb-3'
							sortOptions={false}
						/>
					) : (
						<FloatingLabel controlId='mortgageStateText' label={MortgageLabels.STATE} className='mb-3'>
							<Form.Control
								type='text'
								placeholder={MortgageLabels.STATE}
								className='texbox-input'
								value={formData.state}
								onChange={(event) => setField('state', event.currentTarget.value.toUpperCase())}
								maxLength={2}
							/>
						</FloatingLabel>
					)}
				</Column>
				<Column lg={6} md={6} className='mb-3 d-flex align-items-center'>
					<Checkbox
						id='isActive'
						checked={formData.isActive}
						onChange={(event) => setField('isActive', event.target.checked)}
						label={MortgageLabels.ACTIVE}
						inline
					/>
				</Column>
			</Row>

			<Row className='mt-2 mb-2'>
				<Column lg={12} md={12} className='d-flex justify-content-end gap-2 pe-2'>
					<Button color='primary' outlined={true} onClick={handleCancel}>
						{MortgageLabels.CANCEL.toUpperCase()}
					</Button>
					<Button color='primary' onClick={handleSave}>
						{MortgageLabels.SAVE.toUpperCase()}
					</Button>
				</Column>
			</Row>
		</Container>
	);
};

export default MortageDetails;